/**
 * The Function of one pour location, as the location dialogs show it
 * (pour create wizard + pour edit page).
 *
 * As in the legacy app, a pour's functions only say what the pour CAN do;
 * each location then has one Function of its own. Only hardware that reads
 * the concrete (ConXagent, ConXsensor, ConXone, TMC) has a choice:
 *   - Temperature (MonitorType '2'),
 *   - Maturity ('1'), only on a pour with maturity
 *     (pourComputesMaturity in calibrationCheck says whether it has). A
 *     maturity location still records and charts its temperature.
 * A concrete-hardware channel in the air is just named so (e.g. "Ambient");
 * ambient readings proper come from Temp & Humidity hardware. Older
 * locations saved as Temperature (ambient) ('2' plus IsAmbient) are still
 * read as such, but it is no longer offered.
 * Site hardware's function follows from the device: a weather station is
 * Onsite Weather Conditions and an ambient temperature & humidity device is
 * External Temperature and Humidity.
 */

/** PourLocation.MonitorType values. */
export const MONITOR_TYPE_MATURITY = '1';
export const MONITOR_TYPE_TEMP_ONLY = '2';

export const TEMPERATURE_ONLY_LABEL = 'Temperature';
export const AMBIENT_LABEL = 'Temperature (ambient)';
export const CONCRETE_MATURITY_LABEL = 'Maturity';

/** A concrete location's Function. 'ambient' is only read from older rows. */
export type LocationFunctionValue = 'temp' | 'ambient' | 'maturity';

/** The dialogs' hardware type names. */
export type LocationHardwareType =
  | 'ConXagent'
  | 'ConXsensors'
  | 'ConXone'
  | 'Weather Station'
  | 'Temperature & Humidity'
  | 'TMC'
  | '';

export type LocationFunctionView =
  /** Pick one of the options. */
  | { kind: 'choice'; options: { value: LocationFunctionValue; label: string }[] }
  /** No choice: the function follows from the hardware. */
  | { kind: 'fixed'; label: string };

/** The Function options of a concrete location; a pour without maturity has
 *  no maturity option. How the pour calculates maturity (e.g.
 *  "Temperature-Time Factor") is named beside the choice, not in it. */
export const locationFunctionOptions = (
  maturityAllowed: boolean,
): { value: LocationFunctionValue; label: string }[] => [
  { value: 'temp', label: TEMPERATURE_ONLY_LABEL },
  ...(maturityAllowed ? [{ value: 'maturity' as const, label: CONCRETE_MATURITY_LABEL }] : []),
];

/** What the dialog shows for the location's Function. */
export const locationFunctionView = (
  hardware: LocationHardwareType,
  maturityAllowed: boolean,
): LocationFunctionView => {
  if (hardware === 'Weather Station') return { kind: 'fixed', label: 'Onsite Weather Conditions' };
  if (hardware === 'Temperature & Humidity') {
    return { kind: 'fixed', label: 'External Temperature and Humidity' };
  }
  return { kind: 'choice', options: locationFunctionOptions(maturityAllowed) };
};

/** The Function a new concrete location starts on. */
export const defaultLocationFunction = (maturityAllowed: boolean): LocationFunctionValue =>
  maturityAllowed ? 'maturity' : 'temp';

/** A saved location's Function, from its MonitorType and IsAmbient. */
export const locationFunctionOf = (
  monitorType?: string | null,
  isAmbient?: boolean | null,
): LocationFunctionValue => {
  if (isAmbient) return 'ambient';
  return (monitorType ?? '').trim() === MONITOR_TYPE_TEMP_ONLY ? 'temp' : 'maturity';
};

/** What a Function saves as. */
export const locationFunctionFields = (
  value: LocationFunctionValue,
): { MonitorType: string; IsAmbient: boolean } => ({
  MonitorType: value === 'maturity' ? MONITOR_TYPE_MATURITY : MONITOR_TYPE_TEMP_ONLY,
  IsAmbient: value === 'ambient',
});

/** The label a location table shows for a Function. */
export const locationFunctionLabel = (value: LocationFunctionValue): string => {
  if (value === 'ambient') return AMBIENT_LABEL;
  if (value === 'temp') return TEMPERATURE_ONLY_LABEL;
  return CONCRETE_MATURITY_LABEL;
};
